import type { CarProfile } from '../cars/CarProfile';

/**
 * WHAT A CAR IS MADE OF, AS A LIST OF FILES.
 *
 * loadCarAudio asks for every sample a profile names, one fetch per file: the rungs of the on and off
 * ladders as 'loop' (FLAC first, the engine's voice is lossless), everything else as a 'shot' (Opus
 * first: doors, wrenches, the pit row, the showroom). The offline download has to ask for exactly the
 * same files, or a car that says "ready without coverage" comes up one door short in a tunnel. So the
 * list is derived from the profile here, once, and both the loader's format order and the download
 * read it. No DOM in this file: vite.config.ts also imports it to weigh each car at build time.
 */
export type SampleKind = 'loop' | 'shot';
export interface SampleFile { file: string; kind: SampleKind }

const DEFAULT_FORMATS = ['flac', 'ogg', 'wav'] as const;

function hasExtension(file: string): boolean {
  return /\.[a-z0-9]{2,4}$/i.test(file);
}

/** Every file the loader will ask for, once each, with the kind it asks for it as. */
export function carSampleFiles(profile: CarProfile): SampleFile[] {
  const s = profile.samples;
  if (!s.basePath) return [];   // a synthetic car: nothing on disk
  const out = new Map<string, SampleFile>();
  const add = (file: string, kind: SampleKind) => { if (!out.has(file)) out.set(file, { file, kind }); };
  // the two ladders the loader fetches as loops come first, so the kind they keep is 'loop'
  for (const l of [...s.on, ...s.off]) add(l.file, 'loop');
  // ...and everything else the profile names is a shot, whatever list it sits in (mid, overlay, sim,
  // extra, the beds, the doors, the showroom). A walk over the object finds a list added tomorrow too.
  const walk = (v: unknown): void => {
    if (!v || typeof v !== 'object') return;
    if (Array.isArray(v)) { for (const x of v) walk(x); return; }
    const o = v as Record<string, unknown>;
    if (typeof o.file === 'string') { add(o.file, 'shot'); return; }
    for (const k of Object.keys(o)) if (k !== 'basePath') walk(o[k]);
  };
  walk(s);
  if (s.sweeps) add('sweeps.json', 'shot');
  return [...out.values()];
}

/**
 * The file names to try for one sample, in the order the loader tries them: a name that already
 * carries its extension is itself; a loop is FLAC first; a shot is Opus first; and only formats this
 * browser says it can decode (`formats`, from preferredFormats()) are in the list at all.
 */
export function candidateNames(file: string, kind: SampleKind, formats: readonly string[] = DEFAULT_FORMATS): string[] {
  if (hasExtension(file)) return [file];
  const pref = kind === 'loop' ? 'flac' : 'ogg';
  const order = [pref, ...formats.filter((f) => f !== pref)].filter((f) => formats.includes(f));
  return order.map((ext) => `${file}.${ext}`);
}
